import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { deviceName, warrantyInWords, LIFECYCLE_LABEL, lifecycleTone, myEquipmentRequest, tagText } from '../lib/assets';
import TagChip from '../components/asset/TagChip';
import '../components/Assets.css';

/**
 * My equipment: the devices the register says this person holds.
 *
 * Two buttons on each, because those are the two things somebody looking at
 * their own laptop wants to say: something is wrong with it, or it is not
 * theirs. "This is not mine" opens a ticket to IT already worded, because a
 * register that is wrong about who holds what is a fault worth a ticket -
 * and it is the cheapest way the register gets corrected.
 *
 * Under "View as" the server answers as the person being viewed, so an
 * administrator sees exactly what Rita sees.
 *
 * `portal`: a contractor's own list (or the one an administrator is looking
 * at through View as), asked for with lib/assets' myEquipmentRequest. Their
 * "Report a problem" and "This is not mine" open the same Create Ticket form
 * staff get (26 October), in the portal's frame, with the device chosen and
 * the words already there; the diagnostics list, which is about staff
 * devices reporting in, is not theirs.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function MyEquipment({ portal = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('company');

  useEffect(() => {
    axios.get(`${API_BASE}/assets/mine`, portal ? myEquipmentRequest() : undefined)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Could not load your equipment.'));
  }, [portal]);

  // A contractor's form takes the device as staff's does (?asset=), and the
  // words as well: a request reads better to its approver when it says what
  // it is about in its first line, and the picker's card under it says which.
  const report = (a) => {
    if (!portal) { navigate(`/create?asset=${a.id}`); return; }
    const q = new URLSearchParams({
      asset: a.id,
      title: `Problem with ${tagText(a)}`,
      description: `About ${tagText(a)} (${deviceName(a)}): `
    });
    navigate(`/create?${q}`);
  };

  const notMine = (a) => {
    const q = new URLSearchParams({
      asset: a.id,
      title: `${tagText(a)} is not mine`,
      description: `The register says I have ${tagText(a)} (${deviceName(a)}), but I do not. `
        + 'Please correct who holds it.'
    });
    navigate(`/create?${q}`);
  };

  // 108: the fixed desks that are this person's, each with its kit. The tab
  // exists only when there is one: hot and unassigned desks never come.
  const desks = (!portal && data?.desks) || [];
  const deskCount = desks.reduce((n, d) => n + (d.kit || []).length, 0);
  const onDesk = tab === 'desk' && desks.length > 0;
  const shown = onDesk ? [] : (tab === 'personal' ? data?.personal : data?.assets) || [];

  const card = (a, { mine = true } = {}) => {
    const w = warrantyInWords(a.warranty_end);
    return (
      <section key={a.id} className="as-panel">
        <div className="as-card-head">
          <TagChip asset={a} />
          <span className="as-chip" data-tone={lifecycleTone(a.lifecycle_status)}>
            {LIFECYCLE_LABEL[a.lifecycle_status]}
          </span>
        </div>
        <p style={{ margin: '0.4rem 0', fontWeight: 600 }}>{deviceName(a)}</p>
        <p className="as-card-line">
          {[a.category_name, a.serial_number ? `Serial ${a.serial_number}` : null, a.location_path]
            .filter(Boolean).join(' · ')}
        </p>
        <p className="as-card-line"><span className="as-chip" data-tone={w.tone}>{w.text}</span></p>
        <div className="as-actions" style={{ marginBottom: 0 }}>
          <button type="button" className="btn btn-primary btn-small"
            onClick={() => report(a)}>Report a problem</button>
          {mine && (
            <button type="button" className="btn btn-plain btn-small" onClick={() => notMine(a)}>
              This is not mine
            </button>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">My equipment</h1>
        <p className="page-description">The devices the firm’s register says you have.</p>
      </div>
      {error && <p className="as-error" role="alert">{error}</p>}
      {data && !data.enabled && !portal && <p className="as-note">This page is not switched on.</p>}
      {data && (data.enabled || portal) && (
        <div className="stg">
          <div className="stg-row" role="tablist" aria-label="Kinds of equipment">
            <button type="button" role="tab" className="stg-tab" data-testid="tab-company"
              data-on={String(tab === 'company')} aria-selected={tab === 'company'} onClick={() => setTab('company')}>
              Company <span className="stg-count">{(data.assets || []).length}</span>
            </button>
            {desks.length > 0 && (
              <button type="button" role="tab" className="stg-tab" data-testid="tab-desk"
                data-on={String(tab === 'desk')} aria-selected={tab === 'desk'} onClick={() => setTab('desk')}>
                Desk <span className="stg-count">{deskCount}</span>
              </button>
            )}
            <button type="button" role="tab" className="stg-tab" data-testid="tab-personal"
              data-on={String(tab === 'personal')} aria-selected={tab === 'personal'} onClick={() => setTab('personal')}>
              Personal <span className="stg-count">{(data.personal || []).length}</span>
            </button>
          </div>
        </div>
      )}
      {data && (data.enabled || portal) && tab === 'personal' && (
        <p className="as-quiet" data-testid="personal-explainer">
          These are your own items. They are recorded here so the firm’s register leaves them out.
        </p>
      )}
      {data && (data.enabled || portal) && onDesk && desks.map((d) => (
        <div key={d.id} data-testid="my-desk">
          <h2 className="as-quiet" style={{ fontWeight: 600 }}>
            {d.name}{d.path ? <span> · {d.path}</span> : null}
          </h2>
          {(d.kit || []).length === 0
            ? <div className="empty-state"><p>Nothing is recorded on this desk yet.</p></div>
            : <div className="as-cards">{d.kit.map((a) => card(a, { mine: false }))}</div>}
        </div>
      ))}
      {data && (data.enabled || portal) && !onDesk && shown.length === 0 && (
        <div className="empty-state"><p>{tab === 'personal' ? 'No personal items are recorded for you.' : 'No equipment is assigned to you yet.'}</p></div>
      )}
      {!onDesk && <div className="as-cards">{shown.map((a) => card(a))}</div>}
    </div>
  );
}
